/**
 * PlayerDraftPage — live snake/pack player draft.
 *
 * URL: /player-draft?game=<id>&role=<home|away>
 *
 * Each pack deals 6 cards. Snake order within the pack: first-picker, other,
 * other, first-picker (then reversed on the next pack). Each side picks 2
 * cards per pack, 2 cards are burned. The last pack may run short when the
 * total pick count isn't a multiple of 4.
 *
 * Pure UI — picks send a `player_draft_pick` WS message; the server is the
 * source of truth for turn order, pack state, and position-coverage rules.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, Wifi, WifiOff, Info, ScrollText, ChevronLeft, ChevronRight } from "lucide-react";

import { useGameSocket } from "@/hooks/useGameSocket";
import { Nav } from "@/components/lobby/primitives";
import { PlayerDetailsDialog } from "@/components/game";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITION_ORDER = ["P", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "DH"];
// Mirrors backend `_REAL_PLAYER_POSITIONS` — positions that require exact-
// match coverage. Flex is a wildcard pick slot.

// Display label for a player's position: "DH/3B" when a DH has a secondary
// field position, specific OF slots ("LF/CF") instead of generic "OF".
function displayPosition(player) {
  if (!player) return "";
  const pos = player.position;
  const natural = player.natural_positions || [];
  if (pos === "DH") {
    const secondary = natural.find(p => p !== "DH" && ["C","1B","2B","3B","SS","LF","CF","RF"].includes(p));
    return secondary ? `DH/${secondary}` : "DH";
  }
  if (pos === "OF") {
    const specific = natural.filter(p => ["LF","CF","RF"].includes(p));
    return specific.length > 0 ? specific.join("/") : "OF";
  }
  return pos;
}
const REAL_POSITIONS = ["P", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF"];

/* ─── Pick clock ────────────────────────────────────────────────────────── */

function PickClock({ startedAt, duration }) {
  const deadline = startedAt && duration ? startedAt + duration * 1000 : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!deadline) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [deadline]);
  if (!deadline) return null;
  const remaining = Math.max(0, Math.ceil((deadline - now) / 1000));
  const tone = remaining <= 5 ? "text-red-400" : remaining <= 10 ? "text-amber-400" : "text-slate-400";
  return <span className={`text-xs font-black tabular-nums ${tone}`}>{remaining}s</span>;
}

/* ─── Pack-rip wrapper ────────────────────────────────────────────────────
   Same back→front flip animation the deck-builder draft uses (see
   `PackCard` in DraftPage). Each card mounts face-down (rotateY 180), the
   `motion.div` rotates the whole slot in with a stagger, and at the
   mid-point of that rotation the inner content swaps from the card back
   image to the live PlayerCard. Ancestor remount via `packSeq` key fires
   this fresh per pack, so the rip moment lands on every new pack. */

function PlayerPackCard({ player, index, onPick, disabled, dim, locked, myTurn, warning }) {
  const [flipped, setFlipped] = useState(false);
  useEffect(() => {
    // Same timing curve as the deck draft for parity. First card lands at
    // 450ms, each subsequent staggered by 130ms — six cards finish around
    // ~1.1s, fitting the "rip together, then they all stand up" feel.
    const t = setTimeout(() => setFlipped(true), 450 + index * 130);
    return () => clearTimeout(t);
  }, [index]);

  return (
    // No `h-full` on the flip wrappers — see the comment on PlayerCard's
    // outer div. Cards size purely by aspect-ratio so all 6 in a pack are
    // identical regardless of warning captions or sibling row heights.
    <motion.div
      initial={{ rotateY: 180, opacity: 0, y: 14 }}
      animate={{ rotateY: 0, opacity: 1, y: 0 }}
      transition={{ delay: 0.15 + index * 0.13, duration: 0.55, ease: "easeInOut" }}
      style={{ transformStyle: "preserve-3d", perspective: 900 }}
    >
      <div style={{ transform: flipped ? "rotateY(0deg)" : "rotateY(180deg)" }}>
        {flipped ? (
          <PlayerCard
            player={player}
            onPick={onPick}
            disabled={disabled}
            dim={dim}
            locked={locked}
            myTurn={myTurn}
            warning={warning}
          />
        ) : (
          // Junk Ball card-back. Same aspect-[5/7] min-h as PlayerCard so
          // the flip transition stays in-place (no size jump).
          <div className="w-full aspect-[5/7] min-h-[200px] rounded-lg border-2 border-amber-500/40 overflow-hidden shadow-md bg-slate-900">
            <img src="/card-back.png" alt="" className="w-full h-full object-cover" draggable={false} />
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ─── Player card ─────────────────────────────────────────────────────────
   Two-sided card with click-to-flip interaction:
     • Front  — parchment lockup (name, position, year, summary stat).
     • Back   — landscape stats table with every trimmed season's columns.
   The whole card spins on Y when clicked; a second click flips back. A
   small "Pick this" button overlays the front while it's your turn so the
   pick action stays one tap away regardless of which face is showing. */

// Stat columns per role — full PlayerStats coverage. The back of the card
// shows every stat the schema tracks for that role; the table sits inside
// an `overflow-x-auto` container so the wide column count scrolls
// horizontally on narrow cards instead of clipping.
const HITTER_COLS = [
  { key: "g",   label: "G",   get: s => s?.games_played ?? "—" },
  { key: "ab",  label: "AB",  get: s => s?.at_bats ?? "—" },
  { key: "r",   label: "R",   get: s => s?.runs ?? "—" },
  { key: "h",   label: "H",   get: s => s?.hits ?? "—" },
  { key: "2b",  label: "2B",  get: s => s?.doubles ?? 0 },
  { key: "3b",  label: "3B",  get: s => s?.triples ?? 0 },
  { key: "hr",  label: "HR",  get: s => s?.home_runs ?? 0 },
  { key: "rbi", label: "RBI", get: s => s?.rbi ?? 0 },
  { key: "bb",  label: "BB",  get: s => s?.walks ?? "—" },
  { key: "so",  label: "SO",  get: s => s?.strikeouts ?? "—" },
  { key: "sb",  label: "SB",  get: s => s?.stolen_bases ?? 0 },
  { key: "ba",  label: "BA",  get: s => s?.batting_average != null
      ? Number(s.batting_average).toFixed(3).replace(/^0/, "")
      : "—" },
  { key: "slg", label: "SLG", get: s => s?.slugging != null
      ? Number(s.slugging).toFixed(3).replace(/^0/, "")
      : "—" },
];
const PITCHER_COLS = [
  { key: "g",   label: "G",   get: s => s?.games_played ?? "—" },
  { key: "gs",  label: "GS",  get: s => s?.games_started ?? "—" },
  { key: "cg",  label: "CG",  get: s => s?.complete_games ?? "—" },
  { key: "sho", label: "SHO", get: s => s?.shutouts ?? "—" },
  { key: "w",   label: "W",   get: s => s?.wins ?? 0 },
  { key: "l",   label: "L",   get: s => s?.losses ?? 0 },
  { key: "sv",  label: "SV",  get: s => s?.saves ?? "—" },
  { key: "ip",  label: "IP",  get: s => s?.innings_pitched != null
      ? Number(s.innings_pitched).toFixed(1)
      : "—" },
  { key: "r",   label: "R",   get: s => s?.runs ?? "—" },
  { key: "er",  label: "ER",  get: s => s?.earned_runs ?? "—" },
  { key: "bb",  label: "BB",  get: s => s?.walks ?? "—" },
  { key: "k",   label: "K",   get: s => s?.strikeouts ?? 0 },
  { key: "era", label: "ERA", get: s => s?.era != null
      ? Number(s.era).toFixed(2)
      : "—" },
];

function PlayerCard({ player, onPick, disabled, picked, locked, dim, myTurn, warning = null }) {
  const [flipped, setFlipped] = useState(false);
  const isPitcher = player?.position === "P";
  const seasonsCount = Array.isArray(player?.seasons) ? player.seasons.length : 0;
  const cols = isPitcher ? PITCHER_COLS : HITTER_COLS;
  // Year label = the span of the player's trimmed career on THIS card.
  // Single trimmed season → "1974"; multi-season trim → "1965–1979".
  // Falls back to `player.year` (the highlighted card year) if the seasons
  // array is empty for any reason.
  const yearLabel = (() => {
    const ys = (player?.seasons || []).map(s => s.year).filter(Number.isFinite);
    if (!ys.length) return player?.year != null ? String(player.year) : "";
    const min = Math.min(...ys);
    const max = Math.max(...ys);
    return min === max ? String(min) : `${min}–${max}`;
  })();

  const handlePick = (e) => {
    e.stopPropagation();
    if (disabled || picked || locked) return;
    onPick?.(player.id);
  };
  const toggleFlip = () => {
    if (locked) return;
    setFlipped(f => !f);
  };

  // Both faces share these classes so the flip stays in-place visually.
  // Border tints with the warning level so picks that would worsen
  // coverage carry a visible cue without disabling the card.
  const borderTone = warning === "critical"
    ? "border-red-500/70 shadow-red-500/20"
    : warning === "at_risk"
      ? "border-amber-500/70 shadow-amber-500/20"
      : "border-stone-400/70 shadow-stone-500/20";
  const faceClasses = `absolute inset-0 rounded-lg border-2 ${borderTone} bg-[#f5ead0] text-black shadow-md overflow-hidden p-3 flex flex-col`;

  return (
    // `aspect-[5/7]` is the single source of truth for card dimensions.
    // We intentionally do NOT also use `h-full` here — combined with the
    // grid's `auto-rows-fr`, an `h-full` chain would stretch the card
    // vertically whenever a sibling row had a warning caption / other
    // content that made the row taller. Anchoring to aspect-ratio only
    // keeps every card the exact same size; any extra row height shows up
    // as empty space below the card (where captions live anyway).
    <div className={`relative w-full aspect-[5/7] min-h-[200px] transition-all
      ${dim || picked || locked ? "opacity-50 saturate-50" : ""}`}
      style={{ perspective: 1200 }}
    >
      <div
        onClick={toggleFlip}
        className={`relative w-full h-full transition-transform duration-500 ${disabled && !flipped ? "cursor-not-allowed" : "cursor-pointer"} ${!disabled ? "hover:-translate-y-0.5" : ""}`}
        style={{ transformStyle: "preserve-3d", transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)" }}
        title={player.name}
      >
        {/* ── Front ─────────────────────────────────────────────────────
            Position top-left; bats/throws top-right (B/T) when known.
            Center: name → divider → year-or-year-range → "Tap to flip".
            We deliberately do NOT show summary stats here — the back has
            the full season table, and showing one or two flavour stats on
            the front led to people mistaking them for the card's actual
            stat line. The year span is the cleanest signal of "this is
            the prime / vintage cut of this player". */}
        <div className={faceClasses} style={{ backfaceVisibility: "hidden" }}>
          <div className="flex items-baseline justify-end font-black text-[10px] tracking-wider">
            <span className="font-mono text-stone-700">
              {[player.bats && `B:${player.bats}`, player.throws && `T:${player.throws}`].filter(Boolean).join(" · ")}
            </span>
          </div>
          <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-2">
            <p className="font-black text-center leading-none tracking-widest"
               style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)" }}>
              {displayPosition(player)}
            </p>
            <h3 className="font-black text-center leading-tight mt-1"
                style={{ fontSize: "clamp(0.85rem, 1.3vw, 1.05rem)" }}>
              {player.name}
            </h3>
            <div className="w-full h-px bg-black mt-2" />
            <p className="text-center font-mono font-black leading-tight mt-2"
               style={{ fontSize: "clamp(0.75rem, 1.1vw, 0.95rem)" }}>
              {yearLabel}
            </p>
            <p className="text-center text-[9px] italic text-stone-600 mt-2">Tap to flip</p>
          </div>
          <div className="flex items-baseline justify-between text-[10px] font-black pt-1">
            <span className="font-mono">{player.team || ""}</span>
            <span className="font-mono">{seasonsCount > 0 ? `${seasonsCount}yr` : ""}</span>
          </div>
        </div>

        {/* ── Back: season stats table ─────────────────────────────────── */}
        <div className={faceClasses} style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
          <div className="flex items-baseline justify-between font-black text-[10px] tracking-wider">
            <span className="truncate pr-2">{player.name}</span>
            <span className="font-mono shrink-0">{player.position}</span>
          </div>
          {/* Both axes scroll: vertical for many seasons, horizontal for
              the full 13-column stat line. YR / TM are sticky-left so the
              row never loses its row-header when scrolling stats sideways.
              Table-collapse + `whitespace-nowrap` keep numbers from
              wrapping into the next column on narrow widths. */}
          <div className="flex-1 min-h-0 mt-2 overflow-auto">
            <table className="text-[8.5px] font-mono text-left leading-tight border-collapse whitespace-nowrap">
              <thead className="font-black sticky top-0 bg-[#f5ead0] z-10">
                <tr className="border-b border-black/60">
                  <th className="pr-1 py-0.5 text-left sticky left-0 bg-[#f5ead0]">YR</th>
                  <th className="pr-1 py-0.5 text-left sticky left-6 bg-[#f5ead0]">TM</th>
                  {cols.map(c => (
                    <th key={c.key} className="px-1 py-0.5 text-right">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(player.seasons || []).map((s, i) => (
                  <tr key={`${s.year}-${i}`} className="border-b border-stone-400/40 last:border-b-0">
                    <td className="pr-1 py-0.5 sticky left-0 bg-[#f5ead0]">{s.year}</td>
                    <td className="pr-1 py-0.5 sticky left-6 bg-[#f5ead0]">{s.team || player.team || ""}</td>
                    {cols.map(c => (
                      <td key={c.key} className="px-1 py-0.5 text-right tabular-nums">
                        {c.get(s.stats)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-center text-[9px] italic text-stone-600 pt-1">Tap to flip back</p>
        </div>
      </div>

      {/* Pick button — overlays the card (above both faces) so the pick
          action stays a single tap whether you've flipped or not. */}
      {!picked && !locked && myTurn && (
        <button
          type="button"
          onClick={handlePick}
          disabled={disabled}
          className={`absolute bottom-1.5 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full text-[10px] font-black tracking-wider uppercase shadow-md transition-all
            ${disabled
              ? "bg-stone-400/60 text-stone-700 cursor-not-allowed"
              : "bg-amber-500 hover:bg-amber-400 text-black"}`}
        >
          {disabled ? "Blocked" : "Pick"}
        </button>
      )}

      {(picked || locked) && (
        <span className={`absolute -top-1.5 -right-1.5 z-10 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full border shadow ${
          picked ? "bg-emerald-500/30 border-emerald-500/60 text-emerald-200"
                 : "bg-red-500/30 border-red-500/60 text-red-200"}`}>
          {picked ? "Picked" : "Locked"}
        </span>
      )}
    </div>
  );
}

/* ─── Roster slot builder ───────────────────────────────────────────────── */

const ROSTER_SLOT_ORDER = ["P", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "DH", "Flex"];

function buildRoster(picks, catalog, positionCounts) {
  const slots = {};
  for (const pos of ROSTER_SLOT_ORDER) {
    const cap = positionCounts[pos] ?? 0;
    if (cap > 0) slots[pos] = { cap, filled: [] };
  }
  const unplaced = [];
  for (const pid of picks) {
    const p = catalog[pid];
    if (!p) { unplaced.push(pid); continue; }
    const nat = (p.natural_positions?.length > 0) ? p.natural_positions : [p.position];
    const expanded = nat.flatMap(pos => pos === "OF" ? ["LF", "CF", "RF"] : [pos]);
    let placed = false;
    for (const pos of expanded) {
      if (slots[pos] && slots[pos].filled.length < slots[pos].cap) {
        slots[pos].filled.push(pid); placed = true; break;
      }
    }
    if (!placed && p.position !== "P" && slots.DH && slots.DH.filled.length < slots.DH.cap) {
      slots.DH.filled.push(pid); placed = true;
    }
    if (!placed && slots.Flex && slots.Flex.filled.length < slots.Flex.cap) {
      slots.Flex.filled.push(pid); placed = true;
    }
    if (!placed) unplaced.push(pid);
  }
  return { slots, unplaced };
}

/* ─── Picks column ──────────────────────────────────────────────────────── */

// Build a flat slotMap key→playerId from buildRoster result.
function rosterToSlotMap(picks, catalog, positionCounts) {
  const { slots } = buildRoster(picks, catalog, positionCounts);
  const map = {};
  for (const pos of ROSTER_SLOT_ORDER) {
    const slot = slots[pos];
    if (!slot) continue;
    for (let i = 0; i < slot.cap; i++) {
      map[`${pos}-${i}`] = slot.filled[i] || null;
    }
  }
  return map;
}

// Year-label for a player based on their (trimmed) seasons array. Single
// season → "1974"; multi-season → "1965–1979". Falls back to `player.year`
// if seasons is empty. Shared between the front of the draft cards and
// the post-draft slot rows so both surfaces show the same vintage label.
function playerYearLabel(player) {
  const ys = (player?.seasons || []).map(s => s?.year).filter(Number.isFinite);
  if (!ys.length) return player?.year != null ? String(player.year) : "";
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  return min === max ? String(min) : `${min}–${max}`;
}

function PicksColumn({ label, picks, catalog, positionCounts, isMe, picksTarget, onPreview }) {
  // Coverage counts for the chip grid.
  const have = {};
  for (const pid of picks) {
    const pos = catalog[pid]?.position;
    if (pos) have[pos] = (have[pos] || 0) + 1;
  }
  const picksMade = picks.length;
  const realPositionsConsumed = REAL_POSITIONS.reduce((sum, pos) =>
    sum + Math.min(have[pos] ?? 0, positionCounts[pos] ?? 0), 0);
  const leftoverNonPitchers = REAL_POSITIONS.reduce((sum, pos) => {
    if (pos === "P") return sum;
    return sum + Math.max(0, (have[pos] ?? 0) - (positionCounts[pos] ?? 0));
  }, 0);
  const dhReq = positionCounts.DH ?? 0;
  const dhTagged = have.DH ?? 0;
  const dhFromTagged = Math.min(dhReq, dhTagged);
  const dhFromLeftover = Math.min(dhReq - dhFromTagged, leftoverNonPitchers);
  const dhFilled = dhFromTagged + dhFromLeftover;
  const flexReq = positionCounts.Flex ?? 0;
  const flexFilled = Math.min(flexReq, Math.max(0, picksMade - realPositionsConsumed - dhFilled));
  const totalUnfilledMinimums = REAL_POSITIONS.reduce((sum, pos) =>
    sum + Math.max(0, (positionCounts[pos] ?? 0) - (have[pos] ?? 0)), 0);
  const remainingPicks = Math.max(0, (picksTarget || 0) - picksMade);
  const warning = totalUnfilledMinimums === 0 ? "ok"
    : remainingPicks <= 3 ? "critical"
    : picksMade >= 8 ? "at_risk" : "ok";

  const cellTone = (req, got) => {
    if (req === 0 && got === 0) return "border-slate-800 text-slate-700";
    if (got >= req) return "border-emerald-500/40 bg-emerald-500/10 text-emerald-300";
    if (warning === "critical") return "border-red-500/50 bg-red-500/10 text-red-300";
    if (warning === "at_risk") return "border-amber-500/50 bg-amber-500/10 text-amber-300";
    return "border-slate-700 bg-slate-800/60 text-slate-400";
  };

  // Slot assignment state — auto-fills on new picks, user-adjustable (isMe).
  const [slotMap, setSlotMap] = useState(() => rosterToSlotMap(picks, catalog, positionCounts));
  const prevPicksRef = useRef(picks);

  // When picks array grows, auto-slot the newly arrived pick(s).
  useEffect(() => {
    const prev = prevPicksRef.current;
    prevPicksRef.current = picks;
    if (picks.length === 0) {
      setSlotMap(rosterToSlotMap([], catalog, positionCounts));
      return;
    }
    const newPids = picks.filter(pid => !prev.includes(pid));
    if (newPids.length === 0) return;
    setSlotMap(prev => {
      const next = { ...prev };
      const assigned = new Set(Object.values(next).filter(Boolean));
      for (const pid of newPids) {
        if (assigned.has(pid)) continue;
        const p = catalog[pid];
        if (!p) continue;
        const nat = (p.natural_positions?.length > 0) ? p.natural_positions : [p.position];
        const expanded = nat.flatMap(pos => pos === "OF" ? ["LF", "CF", "RF"] : [pos]);
        let placed = false;
        for (const pos of expanded) {
          const cap = positionCounts[pos] ?? 0;
          for (let i = 0; i < cap && !placed; i++) {
            const key = `${pos}-${i}`;
            if (!next[key]) { next[key] = pid; placed = true; }
          }
          if (placed) break;
        }
        if (!placed && p.position !== "P") {
          for (let i = 0; i < dhReq && !placed; i++) {
            const key = `DH-${i}`;
            if (!next[key]) { next[key] = pid; placed = true; }
          }
        }
        if (!placed) {
          for (let i = 0; i < flexReq && !placed; i++) {
            const key = `Flex-${i}`;
            if (!next[key]) { next[key] = pid; placed = true; }
          }
        }
        if (placed) assigned.add(pid);
      }
      return next;
    });
  }, [picks, catalog, positionCounts, dhReq, flexReq]);

  // Find an empty Flex-* slot (or create the next one) and move `pid` there.
  // Pitchers don't go to the position-player bench — they just become
  // unslotted and are selectable from any other P-* slot's dropdown.
  const benchInPlace = (next, pid) => {
    if (!pid) return;
    const card = catalog[pid];
    if (!card || card.position === "P") return;
    const flexKeys = Object.keys(next).filter(k => k.startsWith("Flex-"));
    const empty = flexKeys.find(k => !next[k]);
    if (empty) {
      next[empty] = pid;
      return;
    }
    const idxs = flexKeys
      .map(k => parseInt(k.split("-")[1], 10))
      .filter(Number.isFinite);
    const maxIdx = idxs.length > 0 ? Math.max(...idxs) : -1;
    next[`Flex-${maxIdx + 1}`] = pid;
  };

  const handleSlotChange = (key, newPid) => {
    setSlotMap(prev => {
      const next = { ...prev };
      const prevPid = prev[key] || null;
      if (newPid) {
        // Picking a new player. If they're sitting in another slot, vacate
        // it (swap-style). The previous occupant of THIS slot is displaced
        // and goes to the bench so the user can see them and pick again.
        for (const [k, v] of Object.entries(next)) {
          if (v === newPid && k !== key) { next[k] = null; break; }
        }
        next[key] = newPid;
        if (prevPid && prevPid !== newPid) benchInPlace(next, prevPid);
      } else {
        // Selecting "—" used to drop the player from view entirely. Now it
        // moves them to a Bench (Flex-*) slot — creating a new row if every
        // existing bench slot is occupied — so nothing on the roster ever
        // disappears from the lobby surface.
        next[key] = null;
        benchInPlace(next, prevPid);
      }
      return next;
    });
  };

  return (
    <div className={`rounded-xl border p-3 ${
      isMe ? "border-amber-500/40 bg-amber-500/5" : "border-slate-700/40 bg-slate-900/40"
    }`}>
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</p>
        <p className="text-xs font-black text-slate-300">{picksMade}{picksTarget ? `/${picksTarget}` : ""}</p>
      </div>
      {/* Coverage chips */}
      <div className="grid grid-cols-5 gap-1 mb-1">
        {POSITION_ORDER.map(pos => {
          const req = positionCounts[pos] ?? 0;
          const got = pos === "DH" ? dhFilled : (have[pos] ?? 0);
          return (
            <div key={pos} className={`text-center rounded px-1 py-0.5 text-[9px] font-black border ${cellTone(req, got)}`}>
              {pos} {got}/{req}
            </div>
          );
        })}
      </div>
      {flexReq > 0 && (
        <div className="mb-2">
          <div className={`text-center rounded px-1 py-0.5 text-[9px] font-black border ${
            flexFilled >= flexReq
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : "border-blue-500/40 bg-blue-500/5 text-blue-300/80"
          }`}>
            BENCH/BP {flexFilled}/{flexReq}
          </div>
        </div>
      )}

      {/* Slotted roster rows */}
      <div className="space-y-0.5 mt-1">
        {ROSTER_SLOT_ORDER.map(pos => {
          const cap = positionCounts[pos] ?? 0;
          // Bench (Flex) grows dynamically: at minimum the configured
          // count, but expand to fit any player benched-in-place via the
          // "—" path in handleSlotChange, plus one trailing empty buffer
          // so the user always has somewhere to drop. Render the section
          // even when the host configured Flex=0 if someone has been
          // benched there.
          let effectiveCap;
          if (pos === "Flex") {
            const flexAssigned = Object.entries(slotMap)
              .filter(([k, v]) => k.startsWith("Flex-") && v);
            const maxAssignedIdx = flexAssigned
              .map(([k]) => parseInt(k.split("-")[1], 10))
              .filter(Number.isFinite)
              .reduce((m, n) => Math.max(m, n), -1);
            effectiveCap = Math.max(cap, maxAssignedIdx + 2);
            if (effectiveCap === 0) return null;
          } else {
            if (cap === 0) return null;
            effectiveCap = cap;
          }
          const slotLabel = pos === "Flex" ? "BN" : pos;
          const isPitcherSlot = pos === "P";
          const isDHOrFlex = pos === "DH" || pos === "Flex";
          // All picks eligible for this slot type (position check only).
          const eligible = picks.filter(id => {
            const p = catalog[id];
            if (!p) return false;
            if (isPitcherSlot) return p.position === "P";
            if (p.position === "P") return false;
            if (isDHOrFlex) return true;
            const nat = (p.natural_positions?.length > 0) ? p.natural_positions : [p.position];
            const exp = new Set(nat.flatMap(np => np === "OF" ? ["LF","CF","RF"] : [np]));
            return exp.has(pos);
          });
          const noEligible = eligible.length === 0;

          return Array.from({ length: effectiveCap }, (_, i) => {
            const key = `${pos}-${i}`;
            const pid = slotMap[key];
            const p = pid ? catalog[pid] : null;

            // "Free" = current pick OR not yet assigned anywhere.
            // "Swap" = eligible but assigned to a different slot.
            const freeOptions = eligible.filter(id =>
              id === pid || !Object.values(slotMap).some(v => v === id)
            );
            const swapOptions = eligible.filter(id =>
              id !== pid &&
              Object.entries(slotMap).some(([k, v]) => v === id && k !== key)
            );

            return (
              <div key={key} className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[10px] ${
                p ? "bg-slate-800/40" : "border border-dashed border-slate-800/60"
              }`}>
                <span className={`font-mono font-black shrink-0 w-7 text-[9px] ${
                  pos === "Flex" ? "text-blue-400/60" : "text-slate-600"
                }`}>{slotLabel}</span>
                {isMe ? (
                  <select
                    value={pid || ""}
                    onChange={e => handleSlotChange(key, e.target.value)}
                    disabled={noEligible}
                    className={`flex-1 min-w-0 bg-slate-900 border border-slate-700/60 rounded px-1 text-[10px] font-bold outline-none truncate focus:border-amber-500/60 ${
                      noEligible
                        ? "text-slate-700 opacity-40 cursor-not-allowed"
                        : "text-slate-300 cursor-pointer"
                    }`}
                  >
                    <option value="">—</option>
                    {freeOptions.map(id => {
                      const op = catalog[id];
                      return (
                        <option key={id} value={id}>
                          {op?.name || id}{op && id !== pid ? ` (${displayPosition(op)})` : ""}
                        </option>
                      );
                    })}
                    {swapOptions.length > 0 && (
                      <optgroup label="↔ Swap from another slot">
                        {swapOptions.map(id => {
                          const op = catalog[id];
                          const fromKey = Object.entries(slotMap).find(([k, v]) => v === id && k !== key)?.[0];
                          const fromLabel = fromKey
                            ? (fromKey.split("-")[0] === "Flex" ? "BN" : fromKey.split("-")[0])
                            : "";
                          return (
                            <option key={id} value={id}>
                              {op?.name || id}{fromLabel ? ` ← ${fromLabel}` : ""}
                            </option>
                          );
                        })}
                      </optgroup>
                    )}
                  </select>
                ) : (
                  p
                    ? <span className="text-slate-300 truncate flex-1" title={p.name}>{p.name}</span>
                    : <span className="text-slate-700 text-[9px]">—</span>
                )}
                {/* Vintage label + card-info button. Shown for any row
                    that has an assigned player on either side (home /
                    away), since each side wants to inspect either the
                    player they slotted or what the opponent slotted. */}
                {p && (
                  <>
                    <span className="text-slate-500 font-mono text-[9px] shrink-0">
                      {playerYearLabel(p)}
                    </span>
                    {onPreview && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onPreview(p); }}
                        title="Career stats"
                        className="p-0.5 text-slate-500 hover:text-amber-400 transition-colors shrink-0"
                      >
                        <Info size={11} />
                      </button>
                    )}
                  </>
                )}
                {!isMe && p && displayPosition(p) !== pos && (
                  <span className="text-slate-600 font-mono text-[8px] shrink-0">{displayPosition(p)}</span>
                )}
              </div>
            );
          });
        })}
      </div>
    </div>
  );
}

/* ─── Draft log drawer ───────────────────────────────────────────────────── */

const DRAFT_LOG_TOGGLE_W   = 36;
const DRAFT_LOG_DEFAULT_W  = 220;
const DRAFT_LOG_MAX_W_RATIO = 0.35;
const DRAFT_LOG_COLLAPSE_AT = 80;
const DRAFT_LOG_LS_KEY      = "junkball_draftLogWidth";

function DraftPickEntry({ pickNum, teamRole, player, isLatest }) {
  const isHome = teamRole === "home";
  const yearLabel = (() => {
    const ys = (player?.seasons || []).map(s => s.year).filter(Number.isFinite);
    if (!ys.length) return player?.year != null ? String(player.year) : "";
    const min = Math.min(...ys), max = Math.max(...ys);
    return min === max ? String(min) : `${min}–${max}`;
  })();
  return (
    <div className={`flex items-start gap-2 px-2 py-1.5 rounded-md transition-colors ${
      isLatest ? "bg-amber-500/10 border border-amber-500/20" : "hover:bg-slate-800/50"
    }`}>
      <div className="shrink-0 flex flex-col items-center gap-0.5 min-w-[22px]">
        <span className={`text-[9px] font-mono font-black px-1 py-0.5 rounded leading-tight w-full text-center ${
          isHome ? "bg-amber-500/20 text-amber-400" : "bg-blue-500/20 text-blue-400"
        }`}>
          {isHome ? "H" : "A"}
        </span>
        <span className="text-[8px] text-slate-600 font-mono leading-none">#{pickNum}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-tight font-bold break-words ${
          isLatest ? "text-amber-200" : "text-slate-300"
        }`}>
          {displayPosition(player)} · {player.name}
        </p>
        {yearLabel && (
          <p className="text-[9px] text-slate-500 font-mono mt-0.5">{yearLabel}</p>
        )}
      </div>
    </div>
  );
}

function DraftLogDrawer({ picksHome, picksAway, catalog }) {
  const [open, setOpen] = useState(false);
  const [expandedW, setExpandedW] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(DRAFT_LOG_LS_KEY) || "", 10);
      if (!isNaN(v) && v >= DRAFT_LOG_COLLAPSE_AT) return v;
    } catch {}
    return DRAFT_LOG_DEFAULT_W;
  });

  useEffect(() => {
    try { localStorage.setItem(DRAFT_LOG_LS_KEY, String(expandedW)); } catch {}
  }, [expandedW]);

  const startResize = useCallback((evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    const isTouch = evt.type === "touchstart";
    const startX = isTouch ? evt.touches[0].clientX : evt.clientX;
    const startW = expandedW;
    const maxW = Math.floor(window.innerWidth * DRAFT_LOG_MAX_W_RATIO);
    const cleanup = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    const onMove = (e) => {
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const proposed = startW + (x - startX);
      if (proposed < DRAFT_LOG_COLLAPSE_AT) { setOpen(false); cleanup(); return; }
      setExpandedW(Math.min(maxW, Math.max(DRAFT_LOG_COLLAPSE_AT, proposed)));
    };
    const onUp = () => cleanup();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  }, [expandedW]);

  // Build pick log grouped by pack (2 picks per team per pack), newest pack first.
  const pickLog = useMemo(() => {
    const maxPacks = Math.max(Math.ceil(picksHome.length / 2), Math.ceil(picksAway.length / 2));
    if (maxPacks === 0) return [];
    const groups = [];
    for (let pack = 1; pack <= maxPacks; pack++) {
      const hi = (pack - 1) * 2;
      const hPicks = picksHome.slice(hi, hi + 2);
      const aPicks = picksAway.slice(hi, hi + 2);
      const entries = [];
      const len = Math.max(hPicks.length, aPicks.length);
      for (let i = 0; i < len; i++) {
        if (hPicks[i]) entries.push({ team: "home", pid: hPicks[i], pickNum: hi + i + 1 });
        if (aPicks[i]) entries.push({ team: "away", pid: aPicks[i], pickNum: hi + i + 1 });
      }
      if (entries.length > 0) groups.push({ pack, entries });
    }
    return groups.reverse(); // newest at top
  }, [picksHome, picksAway]);

  const totalPicks = picksHome.length + picksAway.length;
  const panelW = open ? expandedW : DRAFT_LOG_TOGGLE_W;

  return (
    <motion.div
      animate={{ width: panelW }}
      initial={false}
      transition={{ type: "spring", stiffness: 380, damping: 34 }}
      className="flex-shrink-0 self-stretch bg-slate-900/95 border-r border-slate-800 flex flex-col overflow-hidden select-none relative"
      style={{ width: panelW, minWidth: DRAFT_LOG_TOGGLE_W }}
    >
      {/* Toggle strip — always visible */}
      <div className={`shrink-0 flex items-center border-b border-slate-800 sticky top-0 z-10 bg-slate-900/95 ${
        open ? "justify-between px-2 py-2" : "justify-center py-2"
      }`}>
        {open && (
          <span className="text-[10px] font-black tracking-widest text-slate-400 whitespace-nowrap truncate pr-1">
            DRAFT LOG
          </span>
        )}
        <button
          onClick={() => setOpen(prev => !prev)}
          title={open ? "Hide draft log" : "Show draft log"}
          className="flex items-center gap-1 p-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700 border border-slate-700/50 text-slate-400 hover:text-amber-400 transition-colors"
        >
          <ScrollText size={13} />
          {!open && totalPicks > 0 && (
            <span className="text-[9px] font-bold text-amber-400 tabular-nums">{totalPicks}</span>
          )}
          {open ? <ChevronLeft size={11} /> : <ChevronRight size={11} />}
        </button>
      </div>

      {/* Entries */}
      {open && (
        <div className="flex-1 min-h-0 overflow-y-auto p-1.5 space-y-0.5">
          {pickLog.length === 0 ? (
            <div className="text-center py-8 text-slate-600 text-[10px]">No picks yet</div>
          ) : pickLog.map(({ pack, entries }) => {
            const isLatestPack = pack === pickLog[0].pack;
            return (
              <div key={pack}>
                <div className="flex items-center gap-2 px-1 pt-3 pb-1 first:pt-1">
                  <span className="text-[9px] font-black uppercase tracking-widest text-amber-400/80 shrink-0">
                    Pack {pack}
                  </span>
                  <div className="flex-1 h-px bg-amber-500/20" />
                </div>
                {entries.map((e, i) => {
                  const p = catalog[e.pid];
                  if (!p) return null;
                  return (
                    <DraftPickEntry
                      key={e.pid}
                      pickNum={e.pickNum}
                      teamRole={e.team}
                      player={p}
                      isLatest={isLatestPack && i === entries.length - 1}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Resize handle on right edge */}
      {open && (
        <div
          role="separator"
          aria-orientation="vertical"
          title="Drag to resize"
          onMouseDown={startResize}
          onTouchStart={startResize}
          className="absolute top-0 right-0 bottom-0 w-2 z-30 cursor-col-resize group"
        >
          <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-1 h-10 rounded-full bg-slate-700 group-hover:bg-amber-500/60 transition-colors" />
        </div>
      )}
    </motion.div>
  );
}

/* ─── Main page ─────────────────────────────────────────────────────────── */

export default function PlayerDraftPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const gameId = params.get("game");
  const role = params.get("role") === "away" ? "away" : "home";

  const [game, setGame] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [phase, setPhase] = useState("drafting");
  const [pool, setPool] = useState([]);
  const [pack, setPack] = useState([]);
  const [packSeq, setPackSeq] = useState(1);
  const [packStatus, setPackStatus] = useState("picking");
  // Shared player-preview state — clicking the info icon on any slot row
  // (home or away column) opens the canonical `PlayerDetailsDialog`. One
  // state at the page level keeps the dialog out of both columns'
  // render trees so opening / closing from either side stays consistent.
  const [previewPlayer, setPreviewPlayer] = useState(null);
  const [picksHome, setPicksHome] = useState([]);
  const [picksAway, setPicksAway] = useState([]);
  const [turn, setTurn] = useState("home");
  const [firstPicker, setFirstPicker] = useState("home");
  const [clockStartedAt, setClockStartedAt] = useState(null);
  const [clockDuration, setClockDuration] = useState(45);
  const [packBurned, setPackBurned] = useState([]);
  const [positionCounts, setPositionCounts] = useState({});
  // Pack-reveal animation gate. Each new pack mounts face-down; after the
  // hold + flip beats this flips true and picks become active.
  const [packRevealed, setPackRevealed] = useState(false);

  const navigatedOutRef = useRef(false);

  // `packRevealed` flips true after every per-card flip in PlayerPackCard
  // has fired. Each card runs `450 + idx*130` ms; for 6 cards the last
  // lands at ~1100ms, so we gate the Pick button + warning captions a
  // touch after that. Resets per new pack so the rip lands fresh.
  useEffect(() => {
    if (packStatus !== "picking") return;
    setPackRevealed(false);
    const t = setTimeout(() => setPackRevealed(true), 1200);
    return () => clearTimeout(t);
  }, [packSeq, packStatus]);

  // Catalog now comes from the game record itself — the server persists the
  // trimmed Player records at init time under `player_draft_catalog`, keyed
  // by id. This guarantees the IDs in the pack/picks resolve to the exact
  // (vintage-trimmed) records both clients should display, which the old
  // `/players/all` fetch couldn't promise (each call generated fresh UUIDs).

  const ingest = useCallback((g) => {
    if (!g) return;
    setGame(g);
    if (g.player_draft_catalog && typeof g.player_draft_catalog === "object") {
      setCatalog(prev => ({ ...prev, ...g.player_draft_catalog }));
    }
    if (g.player_draft_phase) setPhase(g.player_draft_phase);
    if (Array.isArray(g.player_draft_pool)) setPool(g.player_draft_pool);
    if (Array.isArray(g.player_draft_pack)) setPack(g.player_draft_pack);
    if (typeof g.player_draft_pack_seq === "number") setPackSeq(g.player_draft_pack_seq);
    if (typeof g.player_draft_pack_status === "string") setPackStatus(g.player_draft_pack_status);
    if (Array.isArray(g.player_draft_picks_home)) setPicksHome(g.player_draft_picks_home);
    if (Array.isArray(g.player_draft_picks_away)) setPicksAway(g.player_draft_picks_away);
    if (g.player_draft_turn) setTurn(g.player_draft_turn);
    if (g.player_draft_pack_first_picker) setFirstPicker(g.player_draft_pack_first_picker);
    if (g.player_draft_clock_started_at !== undefined) setClockStartedAt(g.player_draft_clock_started_at);
    if (typeof g.player_draft_clock_duration === "number") setClockDuration(g.player_draft_clock_duration);
    if (Array.isArray(g.player_draft_pack_burned)) setPackBurned(g.player_draft_pack_burned);
    if (g.player_draft_position_counts) setPositionCounts(g.player_draft_position_counts);
  }, []);

  // Initial fetch.
  useEffect(() => {
    if (!gameId) return;
    axios.get(`${API}/games/${gameId}`).then(r => ingest(r.data)).catch(() => {});
  }, [gameId, ingest]);

  const handleMessage = useCallback((msg) => {
    if (msg.type === "player_draft_state") {
      ingest({ ...(game || {}), ...msg });
    } else if (msg.type === "player_draft_complete") {
      setPhase("complete");
    } else if (msg.type === "game_state" && msg.game) {
      ingest(msg.game);
    }
  }, [game, ingest]);

  const { send: wsSend, connected } = useGameSocket(gameId, role, handleMessage);

  // Once the draft completes, route back to the online-setup lobby — the
  // rosters are now populated and the team-picker columns will unlock.
  useEffect(() => {
    if (phase === "complete" && gameId && !navigatedOutRef.current) {
      navigatedOutRef.current = true;
      // Slight delay so the "Draft Complete" beat is visible.
      const t = setTimeout(() => navigate(`/online-setup?game=${gameId}&role=${role}`), 1500);
      return () => clearTimeout(t);
    }
  }, [phase, gameId, role, navigate]);

  // Safety: if the host resets the draft while we're on this page, phase
  // flips back to "idle". Navigate back to the lobby immediately.
  useEffect(() => {
    if (game && phase === "idle") {
      navigate(`/online-setup?game=${gameId}&role=${role}`, { replace: true });
    }
  }, [game, phase, gameId, role, navigate]);

  // Per-spec the Pick action is NEVER blocked by coverage. We still compute
  // a "would this pick leave a positional gap" hint so the card surfaces an
  // orange/red warning tooltip on hover when picks 9+ / final 3 are at risk.
  const myPicks = role === "home" ? picksHome : picksAway;
  const picksTarget = Object.values(positionCounts).reduce((a, b) => a + (b || 0), 0);
  const myPositions = myPicks.map(pid => catalog[pid]?.position).filter(Boolean);
  const picksMade = myPositions.length;
  // Roster-wide warning level (lights up the coverage grid). Only REAL
  // position minimums (P, C, 1B, …, RF) trigger warnings — DH and Flex
  // are wildcard slots that auto-fill from leftover picks (DH = any
  // leftover non-pitcher, Flex = any leftover pick) so they never urge
  // the user mid-draft.
  const coverageWarningLevel = (() => {
    let unfilled = 0;
    for (const pos of REAL_POSITIONS) {
      const req = positionCounts[pos] || 0;
      if (!req) continue;
      const have = myPositions.filter(p => p === pos).length;
      if (have < req) unfilled += req - have;
    }
    if (unfilled === 0) return "ok";
    if (picksMade >= Math.max(0, picksTarget - 3)) return "critical";
    if (picksMade >= 8) return "at_risk";
    return "ok";
  })();
  // Per-card warning: "would taking THIS player worsen / not address an
  // unfilled real-position gap once warnings have started?" DH-tagged
  // players never trigger a warning since picking them doesn't worsen
  // any real-position coverage (DH isn't a real position).
  const cardWarningFor = useCallback((player) => {
    if (!player) return null;
    if (coverageWarningLevel === "ok") return null;
    if (player.position === "DH") return null;
    const req = positionCounts[player.position] || 0;
    const have = myPositions.filter(p => p === player.position).length;
    if (req > 0 && have < req) return null;  // helps coverage
    return coverageWarningLevel;  // "at_risk" or "critical"
  }, [coverageWarningLevel, positionCounts, myPositions]);

  const myTurn = turn === role && packStatus === "picking" && packRevealed;

  const handlePick = useCallback((playerId) => {
    if (!myTurn) return;
    wsSend({ type: "player_draft_pick", player_id: playerId });
  }, [myTurn, wsSend]);

  if (!gameId) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col">
        <Nav />
        <div className="flex-1 flex items-center justify-center pt-20">
          <p className="text-sm text-slate-500">Missing game id.</p>
        </div>
      </div>
    );
  }

  const totalPicks = picksHome.length + picksAway.length;
  const totalTarget = picksTarget * 2;
  const renderingReveal = packStatus === "burn_reveal";

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Nav />
      {/* flex row: log drawer (always present, pushes content) + scrollable content */}
      <div className="flex pt-20 min-h-screen">
        <DraftLogDrawer picksHome={picksHome} picksAway={picksAway} catalog={catalog} role={role} />
        <div className="flex-1 min-w-0 overflow-y-auto pb-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <p className="text-xs font-bold text-amber-400 tracking-widest uppercase mb-1">
              Player Draft · Snake
            </p>
            <h1 className="text-3xl font-black">Pick {totalPicks + 1} of {totalTarget}</h1>
            <p className="text-slate-500 text-sm mt-1">
              Pack {packSeq} · {firstPicker === role ? "You pick first this pack" : "Opponent picks first this pack"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <PickClock startedAt={clockStartedAt} duration={clockDuration} />
            {connected ? <Wifi size={14} className="text-emerald-400" /> : <WifiOff size={14} className="text-slate-600" />}
            <span className={`px-3 py-1 rounded-lg text-xs font-black tracking-wide
              ${renderingReveal ? "bg-slate-800 border border-slate-700 text-slate-400"
                : myTurn ? "bg-amber-500/20 border border-amber-500/40 text-amber-300"
                         : "bg-slate-800 border border-slate-700 text-slate-400"}`}>
              {renderingReveal ? "Burn reveal…" : myTurn ? "Your pick" : `${turn === "home" ? "Home" : "Away"} is picking…`}
            </span>
          </div>
        </div>

        {/* Pack */}
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-6">
          <div className="flex items-baseline justify-between mb-3">
            <p className="text-[10px] font-black uppercase tracking-widest text-amber-400">
              Pack #{packSeq}
            </p>
            <p className="text-[10px] text-slate-500">
              {renderingReveal ? "4 cards burned — next pack opens shortly"
                : !packRevealed ? "Ripping the pack…"
                : "Snake order: pick when your turn lights up"}
            </p>
          </div>
          {/* 3 cols × 2 rows gives each card ~2× the area of the old 6-up
              layout, leaving room for the stats-table back face. */}
          {/* `packSeq` keyed so the back→front flip re-mounts fresh each
              pack — the rip moment lands every time. */}
          {/* `max-w-3xl mx-auto` caps the pack grid so 6-up cards stay
              roughly trading-card-sized (~220px wide) instead of stretching
              to fill the lobby's `max-w-6xl` shell on wide screens. */}
          <div key={`${packSeq}-${packStatus}`} className="grid grid-cols-2 sm:grid-cols-3 gap-4 auto-rows-fr max-w-3xl mx-auto">
            {(renderingReveal ? packBurned : pack).map((pid, idx) => {
              const p = catalog[pid];
              if (!p) {
                return (
                  <div key={pid} className="aspect-[5/7] min-h-[200px] rounded-lg bg-slate-800 animate-pulse" />
                );
              }
              const warning = cardWarningFor(p);
              return (
                <div key={pid} className="relative">
                  <PlayerPackCard
                    player={p}
                    index={idx}
                    onPick={handlePick}
                    disabled={!myTurn}
                    dim={renderingReveal}
                    locked={renderingReveal}
                    myTurn={myTurn}
                    warning={warning}
                  />
                  {/* Burn stamp overlay sits above the flipped card during
                      the burn-reveal window — the dark wash + angled stamp
                      makes the loss read as a real sacrifice. */}
                  {renderingReveal && (
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                      <div className="absolute inset-0 rounded-lg bg-black/40" />
                      <span className="relative font-black text-red-300 text-2xl tracking-[0.4em] uppercase rotate-[-10deg] border-4 border-red-400/70 px-3 py-1 rounded-md shadow-2xl bg-red-900/30">
                        Burned
                      </span>
                    </div>
                  )}
                  {!renderingReveal && packRevealed && warning === "critical" && (
                    <p className="text-[9px] font-bold text-red-400 text-center mt-1">
                      Final picks — coverage gap risk
                    </p>
                  )}
                  {!renderingReveal && packRevealed && warning === "at_risk" && (
                    <p className="text-[9px] text-amber-400/80 italic text-center mt-1">
                      Coverage at risk
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Picks columns */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <PicksColumn label="Home" picks={picksHome} catalog={catalog} positionCounts={positionCounts} isMe={role === "home"} picksTarget={picksTarget} onPreview={setPreviewPlayer} />
          <PicksColumn label="Away" picks={picksAway} catalog={catalog} positionCounts={positionCounts} isMe={role === "away"} picksTarget={picksTarget} onPreview={setPreviewPlayer} />
        </div>

        {phase === "complete" && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-center">
            <p className="text-emerald-400 font-black text-lg">⚾ Draft Complete</p>
            <p className="text-slate-400 text-xs mt-1 inline-flex items-center gap-1">
              <Loader2 size={11} className="animate-spin" /> Returning to lobby…
            </p>
          </div>
        )}

        <p className="text-[10px] text-slate-700 text-center mt-6">
          Pool: {pool.length} players remaining
        </p>
      </div>
        </div>{/* end scrollable content */}
      </div>{/* end flex row */}

      <PlayerDetailsDialog
        open={!!previewPlayer}
        onClose={() => setPreviewPlayer(null)}
        player={previewPlayer}
      />
    </div>
  );
}
